import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControl, FormControlLabel, FormLabel, Paper, Radio, RadioGroup, Typography,
} from '@mui/material';
import { InventorySettingsService } from '../../services/inventorySettings.service';
import { InventorySettings, LotCostingMethod } from '../../interfaces/inventory/inventorySettings.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

export const METHOD_LABEL: Record<LotCostingMethod, string> = {
  FIFO: 'FIFO by lot',
  AVERAGE: 'Moving average',
};

/** Dhaka wall-clock rendering of the switch instant. */
export const formatSwitchDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Dhaka', dateStyle: 'medium', timeStyle: 'short' }) : '';

/**
 * G6a: inventory costing settings. Lot-tracked items are valued either FIFO by lot (each lot at its receipt cost,
 * the default) or at the store x item moving average (lots still drive quantity, FEFO, expiry and serials).
 */
const InventorySettingsPage: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const canManage = usePermission(PERMISSIONS.INVENTORY_SETTINGS_MANAGE);
  const [settings, setSettings] = useState<InventorySettings | null>(null);
  const [choice, setChoice] = useState<LotCostingMethod>('FIFO');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await InventorySettingsService.get(institutionId!);
        setSettings(s);
        setChoice(s.lotCostingMethod);
      } catch (e) { toast.showApiError(e, 'Failed to load the inventory settings'); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!settings) {
    return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  }

  const changed = choice !== settings.lotCostingMethod;
  const blockedByStocktake = settings.openStocktakes > 0;
  const blockedBackToFifo = choice === 'FIFO' && settings.lotCostingMethod === 'AVERAGE' && settings.lotTrackedItemsOnHand > 0;
  const blockReason = !changed ? null
    : blockedByStocktake ? `${settings.openStocktakes} stocktake(s) are still open. Post or cancel them before changing the costing method.`
    : blockedBackToFifo ? `Lot-tracked stock is on hand for ${settings.lotTrackedItemsOnHand} item(s). Switching back to FIFO needs every lot-tracked item at zero stock.`
    : null;

  const save = async () => {
    try {
      setSaving(true);
      const s = await InventorySettingsService.switchMethod(institutionId!, choice);
      setSettings(s);
      setChoice(s.lotCostingMethod);
      toast.showSuccess(`Lot costing is now ${METHOD_LABEL[s.lotCostingMethod]}`);
    } catch (e) { toast.showApiError(e, 'Failed to change the costing method'); } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="store.invSettings.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Inventory Settings</Typography>
      <Paper data-help-section="store.invSettings.costing" sx={{ p: 2, maxWidth: 760 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Costing of lot-tracked items</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="costing-current">
          Current: <strong>{METHOD_LABEL[settings.lotCostingMethod]}</strong>
          {settings.methodChangedAt ? ` since ${formatSwitchDate(settings.methodChangedAt)}` : ' (default)'}
          . Items without lot tracking always use the moving average.
        </Typography>
        <FormControl disabled={!canManage || saving}>
          <FormLabel id="lot-costing-method">Method</FormLabel>
          <RadioGroup aria-labelledby="lot-costing-method" value={choice}
            onChange={e => setChoice(e.target.value as LotCostingMethod)}>
            <FormControlLabel data-help="store.invSettings.field.fifo" value="FIFO" control={<Radio />}
              label="FIFO by lot: each lot is relieved at its own receipt cost (best for shade or dye lots)" />
            <FormControlLabel data-help="store.invSettings.field.average" value="AVERAGE" control={<Radio />}
              label="Moving average: lots still decide which stock is picked (FEFO, expiry, serials), but cost is the store's average" />
          </RadioGroup>
        </FormControl>
        {!canManage && (
          <Alert severity="info" sx={{ mt: 2 }}>Changing the method needs the Inventory settings permission.</Alert>
        )}
        {blockReason && <Alert severity="warning" sx={{ mt: 2 }} data-testid="costing-block">{blockReason}</Alert>}
        {canManage && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <Button data-help="store.invSettings.change" variant="contained" disabled={!changed || !!blockReason || saving} onClick={() => setConfirmOpen(true)}>
              Change method
            </Button>
          </Box>
        )}
      </Paper>
      <Dialog data-help-page="store.invSettings.confirm" open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)}>
        <DialogTitle>Switch to {METHOD_LABEL[choice]}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {choice === 'AVERAGE'
              ? 'From now on lot-tracked stock is relieved at the store average. Past movements keep their cost. Switching back to FIFO is only possible once every lot-tracked item is at zero stock.'
              : 'From now on each lot is relieved at its own receipt cost again.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-help="store.invSettings.confirmCancel" onClick={() => setConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button data-help="store.invSettings.confirmSwitch" variant="contained" onClick={save} disabled={saving}>Switch</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventorySettingsPage;
